import { UniqueIdentifier } from '@dnd-kit/core'
import { Done, DoneAll } from '@mui/icons-material'
import { Box, Card, Stack, Tooltip, Typography } from '@mui/material'
import { CarrierVisitDirection } from '@planning/app/api'
import { formatRailcarName } from '@planning/utils/railcar-utils'
import { useTranslate } from '@tolgee/react'
import { useMinimalsTheme } from '@tom-ui/ui'
import { computed } from 'mobx'
import { WarningIcon } from 'modules/ui/src/theme/overrides/CustomIcons'
import { FC } from 'react'
import { RailcarPlanningViewStore } from '../../Stores/RailcarPlanningViewStore'
import { RailcarPlanningContainerProps } from './RailcarPlanningContainer'
import { RailcarPlanningRailcarMenu } from './RailcarPlanningRailcarMenu'

type Props = {
  railcarPlanningViewStore: RailcarPlanningViewStore
  railcar: RailcarPlanningRailcarProps
  filter?: string | null
}

export type RailcarPlanningRailcarProps = {
  id: UniqueIdentifier
  railTrackId: string
  railcarTrackPositionId: number
  number: string
  containers?: RailcarPlanningContainerProps[]
  sequence?: number
  railcarMaxTEU?: number | null
  hasOrdersWithDifferentPodsAssigned?: boolean
  hasExceedingTEU?: boolean
  isChecked?: boolean
  isPrepped?: boolean
  isGap?: boolean
  isMissingRailcar?: boolean
}

export const RailcarPlanningRailcar: FC<Props> = props => {
  const { railcar, filter, railcarPlanningViewStore } = props
  const { t } = useTranslate()
  const theme = useMinimalsTheme()
  const railcarNumberMatchesFilter =
    filter && railcar.number.toLowerCase().includes(filter.toLowerCase())
  const hasContainerMatchingFilter =
    filter &&
    railcar.containers?.some(container =>
      container.number.toLowerCase().includes(filter.toLowerCase()),
    )

  const railcarMatchesFilter = railcarNumberMatchesFilter || hasContainerMatchingFilter

  const warningTooltip = computed(() => {
    const diffPodWarningMessage = t(
      'differentPodsOnTheSameRailcar',
      'Different PODs on the same railcar',
    )
    const exceedingTeuWarningMessage = t(
      'exceedingMaximumTeuCapacity',
      'Exceeding maximum TEU capacity',
    )

    const isMissingRailcar = t('missingRailcar', 'Missing railcar')

    const composedMessage = (
      <ul style={{ listStyleType: 'disc', padding: '4px 4px 4px 16px', margin: 0 }}>
        {railcar.hasOrdersWithDifferentPodsAssigned && <li>{diffPodWarningMessage}</li>}
        {railcar.hasExceedingTEU && <li>{exceedingTeuWarningMessage}</li>}
        {railcar.isMissingRailcar && <li>{isMissingRailcar}</li>}
      </ul>
    )

    return composedMessage
  }).get()

  return (
    <Card
      sx={{
        border: '1px solid',
        backgroundColor: railcar.isGap ? theme.palette.grey[200] : '#fff',
        borderColor: railcarNumberMatchesFilter
          ? theme.palette.info.main
          : railcar.isGap
            ? theme.palette.grey[300]
            : theme.palette.grey[400],
        minWidth: railcar.isGap ? '180px' : '360px',
        opacity: railcarMatchesFilter || !filter ? 1 : 0.3,
        height: '50px',
        placeContent: 'center',
      }}
    >
      <Stack
        direction='row'
        sx={{
          padding: theme.customSpacing.xxs,
          paddingLeft: theme.customSpacing.s,
        }}
        justifyContent='space-between'
      >
        <Stack
          direction='row'
          spacing={theme.customSpacing.m}
          alignItems='center'
          ml={theme.customSpacing.xxs}
          mr={theme.customSpacing.m}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: railcar.isGap ? theme.palette.grey[500] : '#111',
              width: '26px',
              height: '26px',
              color: '#fff',
              padding: '0px',
              borderRadius: '4px',
              fontSize: '12px',
            }}
          >
            {railcar.sequence}
          </Box>
          {!railcar.isGap && <Stack flexGrow={1}>{formatRailcarName(railcar.number)}</Stack>}
          {railcar.isGap && (
            <Typography color='textDisabled' flexGrow={1}>
              <Stack>{t('notAllocated', 'Not allocated')}</Stack>
            </Typography>
          )}
        </Stack>
        <Stack direction='row' alignItems='center' spacing={theme.customSpacing.xxxs}>
          <Stack direction='row' alignItems='center' spacing={theme.customSpacing.s}>
            {(railcar.hasOrdersWithDifferentPodsAssigned ||
              railcar.hasExceedingTEU ||
              railcar.isMissingRailcar) && (
              <Tooltip title={warningTooltip}>
                <Stack color={theme.palette.warning.main}>
                  <WarningIcon />
                </Stack>
              </Tooltip>
            )}
            {!!railcar.railcarMaxTEU && (
              <Box
                sx={{
                  background: theme.palette.grey[200],
                  padding: '4px 8px',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '4px',
                }}
              >
                <Typography variant='subtitle2' fontSize={12}>
                  {t('teuMax', '{TEU} TEU max', { TEU: railcar.railcarMaxTEU })}
                </Typography>
              </Box>
            )}
            {railcar.isPrepped && !railcar.isChecked && (
              <Tooltip
                title={
                  railcarPlanningViewStore.direction === CarrierVisitDirection.Inbound
                    ? t('dischargePrepped', 'Discharge prepped')
                    : t('loadPrepped', 'Load prepped')
                }
              >
                <Done />
              </Tooltip>
            )}
            {railcar.isChecked && (
              <Tooltip title={t('departureChecked', 'Departure checked')}>
                <DoneAll />
              </Tooltip>
            )}
          </Stack>

          {!railcar.isGap && (
            <RailcarPlanningRailcarMenu
              railcarPlanningViewStore={props.railcarPlanningViewStore}
              railcar={railcar}
            />
          )}
        </Stack>
      </Stack>
    </Card>
  )
}
